import AppContext from 'App/AppContext';
import { IPStack, IPText } from 'baseComponents';
import HrefLink from 'components/Links/HrefLink';
import TextInfo from 'components/Notification/Info';
import { StateIcon, StateIconSize, StateIconState } from 'components/Utils/IconsAndImages/StateIcon';
import { SeverityBar } from 'components/Utils/SeverityBar';
import {
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalStackTokensGapTileRow,
  globalTextStylesBold,
  globalTextStylesError,
} from 'globalStyles';
import { TagSystemTypes } from 'models/tag';
import { TaskColors } from 'models/tasks/task';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

export interface ITileTrend {
  value: number; // 0 => equal, >0 => higher, <0 => lower
  reverse?: boolean; // when true, a positive trend is bad (red) and a negative trend is good (green)
  asPercent?: boolean;
}

interface IMonitoringOverview {
  content: string;
  hasOpenTasks: boolean;
  onClick?: () => void;
  trend?: ITileTrend;
}

export interface ITaskEffectivenessLabels {
  monitoringReliability?: string;
  deviations?: string;
  riskConsequences?: string;
}

interface ITaskEffectivenessOverviewProps {
  title: string;
  info: string;
  totalOpenCount?: number; // when omitted, the header shows no open-task status
  openTasksLink?: string;
  tagSystemTypeCount?: Partial<Record<TagSystemTypes, number>>;
  isUpdating: boolean;
  monitoring?: IMonitoringOverview;
  trends?: Partial<Record<TagSystemTypes, ITileTrend>>; // trend per count tile (findings, non-conformities, incidents)
  labels?: ITaskEffectivenessLabels; // overrides for the layer headers; the defaults refer to controls
  tileWidth?: number;
  onCategoryClick?: (tagSystemType: TagSystemTypes) => void;
  children?: ReactNode; // extra tiles that flow in the same wrapping row as the effectiveness layers
}

export const TaskEffectivenessOverview = (props: ITaskEffectivenessOverviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'control']);
  const tileWidth = props.tileWidth ?? 150;
  const openTasksLink = props.openTasksLink;

  const getBackgroundColor = (hasNotAccepted: boolean): string => {
    if (hasNotAccepted) {
      return appContext.useDarkMode ? TaskColors.DarkOverDeadline : TaskColors.OverDeadline;
    }

    return appContext.useDarkMode ? TaskColors.DarkCompleted : TaskColors.Completed;
  };

  const getCount = (tagSystemType: TagSystemTypes) => props.tagSystemTypeCount?.[tagSystemType] ?? 0;

  const getCategoryClick = (tagSystemType: TagSystemTypes): (() => void) | undefined => {
    const onCategoryClick = props.onCategoryClick;
    if (!onCategoryClick) return undefined;

    return () => onCategoryClick(tagSystemType);
  };

  const renderCountTile = (tagSystemType: TagSystemTypes, label: string) => {
    const count = getCount(tagSystemType);
    const trend = props.trends?.[tagSystemType];

    return (
      <SeverityBar
        content={count}
        label={label}
        color={getBackgroundColor(count > 0)}
        width={tileWidth}
        isUpdating={props.isUpdating}
        onClick={getCategoryClick(tagSystemType)}
        showLabelAsLink={true}
        trendLine={trend?.value}
        trendReverse={trend?.reverse}
        trendAsPercent={trend?.asPercent}
      />
    );
  };

  return (
    <IPStack tokens={globalStackTokensGapMedium}>
      <IPStack horizontal verticalAlign="start" horizontalAlign="space-between">
        <IPStack.Item>
          <TextInfo message={props.info}>
            <IPText variant="mediumPlus" styles={globalTextStylesBold}>
              {props.title}
            </IPText>
          </TextInfo>
        </IPStack.Item>
        {!props.isUpdating && (props.totalOpenCount ?? 0) > 0 && openTasksLink && (
          <IPStack.Item>
            <HrefLink underline href={openTasksLink} styles={globalTextStylesError}>
              <IPText styles={globalTextStylesError}>
                {t('control:TabDashboard.AllOpenTasks', { count: props.totalOpenCount })}
              </IPText>
            </HrefLink>
          </IPStack.Item>
        )}
        {!props.isUpdating && props.totalOpenCount === 0 && (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <StateIcon state={StateIconState.Success} size={StateIconSize.Medium} />
            <IPText styles={globalTextStylesBold}>{t('control:TabDashboard.NoOpenTasks')}</IPText>
          </IPStack>
        )}
      </IPStack>
      <IPStack horizontal wrap verticalAlign="start" tokens={globalStackTokensGapTileRow}>
        {props.monitoring && (
          <IPStack tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              <IPText styles={globalTextStylesBold}>
                {props.labels?.monitoringReliability ?? t('control:TabDashboard.ControlEffectivessLayer1')}
              </IPText>
            </IPStack.Item>
            <IPStack.Item>
              <SeverityBar
                content={props.monitoring.content}
                label={t('control:TabDashboard.Completed')}
                color={getBackgroundColor(props.monitoring.hasOpenTasks)}
                width={tileWidth}
                isUpdating={props.isUpdating}
                onClick={props.monitoring.onClick}
                showLabelAsLink={true}
                trendLine={props.monitoring.trend?.value}
                trendReverse={props.monitoring.trend?.reverse}
                trendAsPercent={props.monitoring.trend?.asPercent}
              />
            </IPStack.Item>
          </IPStack>
        )}
        <IPStack tokens={globalStackTokensGapMedium}>
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>
              {props.labels?.deviations ?? t('control:TabDashboard.ControlEffectivessLayer2')}
            </IPText>
          </IPStack.Item>
          <IPStack.Item>
            {renderCountTile(TagSystemTypes.MonitoringFinding, t('control:TabDashboard.MonitoringFindings'))}
          </IPStack.Item>
        </IPStack>
        {/* Risk consequences: non-conformities and incidents belong to the same layer and move together when wrapping */}
        <IPStack tokens={globalStackTokensGapMedium}>
          <IPStack.Item>
            <IPText styles={globalTextStylesBold}>
              {props.labels?.riskConsequences ?? t('control:TabDashboard.ControlEffectivessLayer3')}
            </IPText>
          </IPStack.Item>
          <IPStack horizontal tokens={globalStackTokensGapMedium}>
            <IPStack.Item>
              {renderCountTile(TagSystemTypes.NonConformity, t('control:TabDashboard.NonConformities'))}
            </IPStack.Item>
            <IPStack.Item>{renderCountTile(TagSystemTypes.Incident, t('control:TabDashboard.Incidents'))}</IPStack.Item>
          </IPStack>
        </IPStack>
        {props.children}
      </IPStack>
    </IPStack>
  );
};
